import { useState, useEffect } from 'react';
import { shapeResult } from '../config';
import { currencyFormatter, percentageFormatter } from '@dnhb/formatters';
import styled from '@emotion/styled';
import useMedia from 'shared/hooks/useMedia';

const Table = styled.table(({theme}) => `
  width: 100%;
  min-width: 340px;
  font-size: 14px;
  color: ${theme.primaryFontColor};
  letter-spacing: -0.4px;
  font-weight: 600;

  table, thead, tbody, tfoot, th, tr, td {
    background-color: ${theme.cardColor};
  }
  
  strong {
    color: ${theme.primaryColor};
  }

  thead {
    border-bottom: 1px solid ${theme.borderColor};
  }

  thead, tfoot {
    td {
      &:nth-of-type(2n+1) {
        text-align: right;
        padding-left: 16px;
        padding-right: 4px;
      }
      &:nth-of-type(2n) {
        padding-left: 4px;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px dashed ${theme.borderColor};
    }

    tr:last-of-type {
      border-style: solid;
    }

    td {
      &:first-of-type {
        width: 50%;
      }
      &:nth-of-type(2n) {
        text-align: right;
        padding-right: 4px;
      }
      &:nth-of-type(2n+1) {
        padding-left: 4px;
      }
    }
  }

  tr {
    height: 48px;
  }
  td {
    vertical-align: middle;
    &:not(:first-of-type) {
      white-space: nowrap;
    }
  }

  strong {
    font-weight: 800;
  }
  
  @media ${theme.mobile} {
    font-size: 12px;
  }
`);

const SuccessLabel = styled.div(({theme}) => `
  display: inline-block;
  color: ${theme.cardColor};
  background-color: ${theme.successColor};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.43px;
  border-radius: 5px;
  line-height: 18px;
  padding: 2px 5px;
  
  &::before {
    content: '- ';
  }
`)

const DetailsTable = ({ result }) => {
  const [moment, setMoment] = useState(null);
  const [upperFirst, setUpperFirst] = useState(null);
  
  useEffect(() => {
    import('lodash/upperFirst').then(u => setUpperFirst(u));
    import('moment').then(m => setMoment(m));
    import('moment/locale/nl');
  }, [])
  const { onMobile } = useMedia();

  if (moment === null || upperFirst === null) {
    return null;
  }

  return ( 
    <Table>
      <thead>
        <tr>
          <td colSpan="2">1e maandlast</td>
          <td><strong>{currencyFormatter(result.first_monthly_payment, 0)}</strong></td>
          <td>1e rente</td>
          <td><strong>{percentageFormatter(result.initial_interest_rate)}</strong></td>
        </tr>
      </thead>
      {result.rate_corrections.length > 0 && (
        <tbody>
          {result.rate_corrections.map(rate => (
            <tr key={rate.date}>
              {onMobile
                ? <td>{upperFirst.default(moment.default(rate.date, 'DD-MM-YYYY').format('MMM YYYY'))}</td>
                : <td>Per {moment.default(rate.date, 'DD-MM-YYYY').format('MMMM YYYY')}</td>
              }
              <td>
                <SuccessLabel>
                  {currencyFormatter(rate.payment_correction, 0)}
                </SuccessLabel>
              </td>
              <td>{currencyFormatter(rate.payment_after_correction, 0)}</td>
              <td>
                <SuccessLabel>
                  {percentageFormatter(rate.rate_correction, 2)}
                </SuccessLabel>
              </td>
              <td>{percentageFormatter(rate.rate_after_correction, 2)}</td>
            </tr>
          ))}
        </tbody>
      )}
      <tfoot>
        <tr>
          <td colSpan="2">Gem. maandlast</td>
          <td><strong>{currencyFormatter(result.average_monthly_payment, 0)}</strong></td>
          <td>Looptijdrente</td>
          <td><strong>{percentageFormatter(result.average_interest_rate)}</strong></td>
        </tr>
      </tfoot>
    </Table>
  );
};

DetailsTable.propTypes = {
  result: shapeResult.isRequired,
};

export default DetailsTable;
